/**
 * Raydium Perps Bot — Multi-bot WebSocket context.
 * Tek WS bağlantısı üzerinden N bota ait veri akar.
 * Sayfalar useWS() ile aktif botun verisini görür — sayfa değişikliği gerekmez.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useToast } from './components/Toast';
import { shouldPlaySound, playNotifSound } from './notifUtils';

const DEV_PORTS = new Set(['3000', '3001', '3131']);
const DEFAULT_WS_URL = DEV_PORTS.has(window.location.port)
  ? `ws://${window.location.hostname}:8866`
  : 'wss://ws.barbarosquant.org/ws';

/**
 * Her WS endpoint için ayrı session token key'i üretir.
 * Problem: Her Python süreci (8866, 8879, 8887...) kendi token havuzunu bellekte tutar.
 * 8866'dan alınan token 8879'da geçersiz — per-endpoint saklama bunu çözer.
 *
 * Örnekler:
 *   wss://ws.barbarosquant.org/ws       → 'rt_token_/ws'
 *   wss://ws.barbarosquant.org/deepblue-ws → 'rt_token_/deepblue-ws'
 *   ws://localhost:8866                  → 'rt_token_8866'  (dev)
 *   ws://localhost:8879                  → 'rt_token_8879'  (dev)
 */
export function wsTokenKey(url: string): string {
  try {
    const u = new URL(url);
    // Dev: port bazlı (localhost:8866 vs localhost:8879 ayırt eder)
    // Prod: pathname bazlı (/ws vs /deepblue-ws vs /hl-ws...)
    const id = u.hostname === 'localhost' ? u.port : u.pathname;
    return `rt_token_${id}`;
  } catch {
    return 'rt_token';
  }
}

// ─── Tipler ───────────────────────────────────────────────────────────────────

export interface TickerData {
  symbol: string; mark_price: number; index_price: number;
  best_bid: number; best_ask: number; spread_pct: number;
  est_funding: number; next_funding: number;
  open_interest: number; volume_24h: number; price_change_24h: number;
}

export interface AccountData {
  totalAccountValue: number; availableBalance: number;
  crossUnrealizedPnl: number; totalInitialMargin: number;
  totalMaintenanceMargin: number; usdc_balance: number;
}

export interface Position {
  symbol: string; side: 'long' | 'short';
  positionQty: number; avgEntryPrice: number; markPrice: number;
  unrealizedPnl: number; pnlPct: number; leverage: number;
  notional: number; estLiqPrice: number | null;
}

export interface OpenOrder {
  order_id: number; symbol: string; side: string;
  type: string; price: number; quantity: number;
  reduce_only: boolean; status: string;
}

export interface TpConfig {
  enabled: boolean;
  type: 'price' | 'percent';
  value: number;
  mode?: 'ladder' | 'drip';
  triggerPrice?: number;
  chunkSize?: number;
  chunkPriceType?: string;
  maxTotalQty?: number;
  pauseStrategyId?: string;   // Tetiklenince durdurulacak kaynak strateji ID'si
}

export interface Strategy {
  id: string; name: string; symbol: string; side: 'long' | 'short';
  orderSize: number; orderCount: number; leverage: number;
  entryCondition: string; entryThreshold: number;
  priceType: string; active: boolean;
  maxPositionSize?: number;
  tpConfig: TpConfig;
  strategyType?: 'standard' | 'tp_only';
}

export interface OrderCounter { [strategy_id: string]: number; }
export interface OrderStats   { sent: number; filled: number; date?: string; }

export interface BotInfo { id: string; name: string; network?: string; }

export interface SymbolSpec {
  tick_size: number;
  step_size: number;
  min_qty: number;
  min_notional: number;
  max_leverage: number;
  // Binance/Aster: kaldıraç tavanı pozisyon büyüklüğüne bağlı. İlk kademenin
  // nominal tavanı ve bir sonraki kademenin kaldıracı — arayüz "maks 50 ·
  // $5.000 üstünde 20" yazabilsin diye taşınıyor. Diğer borsalarda yok.
  lev_cap_notional?: number;
  lev_next?: number;
  // KuCoin CROSS: borsa emirdeki kaldıracı yok sayıp hesap ayarını uyguluyor.
  // Bu alan GERÇEKTEN uygulanan değer; strateji/emir kaldıracı geçersiz kalır.
  effective_leverage?: number;
}
/** symbol → SymbolSpec (aktif botun piyasa bilgisi) */
export type MarketInfo = Record<string, SymbolSpec>;

export interface AlertItem {
  id: string;
  symbol: string;
  condition: string;
  threshold: number;
  note: string;
  one_shot: boolean;
  active: boolean;
  created_at: number;
  triggered_at: number | null;
}

// ─── Per-bot state ────────────────────────────────────────────────────────────

interface BotState {
  account:       AccountData | null;
  positions:     Position[];
  openOrders:    OpenOrder[];
  tickers:       TickerData[];
  orderStats:    OrderStats;
  orderCounters: OrderCounter;
  strategies:    Strategy[];
  lastUpdate:    string;
  pairTrades:    any[];
  favorites:     string[];
  // COIN-M (salt okunur) — yalnız Binance robotunda dolu. Ayrı robot değil,
  // Pozisyonlar sayfasındaki "COIN-M" sekmesinin verisi (binance_cm.py).
  coinm?:        any;
  // Köprü sağlığı. Sunucu yalnız DURUM DEĞİŞİMİNDE gönderiyor.
  bridge:        { ok: boolean; error?: string; action?: string };
  // Borsa websocket akışından gelen anlık mark fiyatı: {sembol: fiyat}.
  // YALNIZ açık pozisyonu olan semboller geliyor — Binance akışı 915 sembol
  // taşıyor, tamamını saniyede bir yollamak ~100 KB/s eder ve tarayıcının
  // hiçbir işine yaramaz. `tickers` (3 sn) evrenin tamamı için kaynak olmaya
  // devam ediyor; bu yalnız onun üstüne yazan taze bir katman.
  marks:         Record<string, number>;
  marksTs:       number;
}

const DEFAULT_BOT_STATE: BotState = {
  account: null, positions: [], openOrders: [], tickers: [], bridge: { ok: true, error: '' },
  orderStats: { sent: 0, filled: 0 }, orderCounters: {},
  strategies: [], lastUpdate: '—', pairTrades: [], favorites: [],
  marks: {}, marksTs: 0,
};

// ─── localStorage yardımcıları ────────────────────────────────────────────────

function loadLocalStrategies(botId: string): Strategy[] {
  try { return JSON.parse(localStorage.getItem(`raydium_strategies_${botId}`) || '[]'); }
  catch { return []; }
}

function saveLocalStrategies(botId: string, strats: Strategy[]) {
  localStorage.setItem(`raydium_strategies_${botId}`, JSON.stringify(strats));
}

// ─── Context tipi ─────────────────────────────────────────────────────────────

interface WSContextValue {
  // Aktif botun verileri (tüm sayfalar bunları kullanır — değişiklik gerekmez)
  connected:       boolean;
  tickers:         TickerData[];
  account:         AccountData | null;
  positions:       Position[];
  openOrders:      OpenOrder[];
  orderCounters:   OrderCounter;
  orderStats:      OrderStats;
  strategies:      Strategy[];
  setStrategies:   React.Dispatch<React.SetStateAction<Strategy[]>>;
  sendWs:          (msg: object) => void;
  sendForBot:      (msg: object, botId: string) => void;
  subscribe:       (fn: (data: any) => void) => () => void;
  lastUpdate:      string;
  pairTrades:      any[];
  coinm:           any;
  switchEndpoint:       (url: string) => void;
  isMainBackend:        boolean;   // false ise ortak veriler (SQLite geçmişi, LP) bu backend'de yok
  clearCurrentToken:    () => void;   // Mevcut endpoint'in token'ını sil (auth failure sonrası)
  // Bot seçici
  botList:         BotInfo[];
  activeBotId:     string;
  setActiveBotId:  (id: string) => void;
  botsState:       Record<string, any>;
  // Alertler (bot_id → alert listesi)
  alerts:          Record<string, AlertItem[]>;
  // Aktif botun favorileri
  favorites:       string[];
  // Aktif botun piyasa bilgisi (symbol → SymbolSpec) — şu an yalnızca AsterDex dolduruyor
  marketInfo:      MarketInfo;
  bridge:          { ok: boolean; error?: string; action?: string };
}

const WSContext = createContext<WSContextValue | null>(null);

export function useWS(): WSContextValue {
  const ctx = useContext(WSContext);
  if (!ctx) throw new Error('useWS must be used inside WSProvider');
  return ctx;
}

// Mesajlar bu tipler için bot_id eklenmez (global)
const GLOBAL_MSG_TYPES = new Set(['auth', 'ping', 'get_notifications', 'set_notifications',
  'get_lp_wallets', 'get_lp_positions', 'get_lp_position_events', 'lp_sync_wallet', 'lp_lookup',
  'set_lp_annotation', 'set_lp_group_status', 'get_navi_snapshots',
  'set_lp_group_trades', 'get_lp_trade_tags', 'get_lp_group_perp',
  'create_lp_group', 'set_group_pending', 'get_group_pending', 'get_perp_groups',
  // Kademe motoru grup bazlı çalışıyor, aktif bota bağlı değil
  'scale_start', 'scale_stop', 'scale_status', 'add_sui_swaps',
  // Fed fiyatlaması botlardan bağımsız (giriş ekranı → Makro sekmesi)
  'get_fed_path', 'get_fed_history',
  // Günlük performans raporu — hesap bazlı değil, tüm hesaplar
  'get_perf_reports', 'get_perf_report_pdf', 'regen_perf_report', 'add_manual_transfer', 'delete_manual_transfer']);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function WSProvider({ children }: { children: React.ReactNode }) {
  const { showToast } = useToast();
  const [connected,    setConnected]    = useState(false);
  const [botsState,    setBotsState]    = useState<Record<string, BotState>>({});
  const [botList,      setBotList]      = useState<BotInfo[]>([]);
  const [activeBotId,  setActiveBotIdState] = useState('');
  const [alerts,       setAlerts]       = useState<Record<string, AlertItem[]>>({});
  // per-bot market info: botId → MarketInfo
  const [botsMarketInfo, setBotsMarketInfo] = useState<Record<string, MarketInfo>>({});

  const wsRef           = useRef<WebSocket | null>(null);
  const reconnectTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimer       = useRef<ReturnType<typeof setInterval> | null>(null);
  const listenersRef    = useRef<Set<(data: any) => void>>(new Set());
  const activeBotIdRef  = useRef('');
  const syncedRef       = useRef<Record<string, boolean>>({});
  const botsStateRef    = useRef<Record<string, BotState>>({});
  const pendingPushRef  = useRef<Record<string, Strategy[]>>({});
  const wsUrlRef        = useRef(DEFAULT_WS_URL);
  const [isMainBackend, setIsMainBackend] = useState(true);   // ortak verileri sunan backend mi
  const connectRef      = useRef<() => void>(() => {});

  // botsStateRef'i state ile senkron tut
  useEffect(() => { botsStateRef.current = botsState; }, [botsState]);

  const subscribe = useCallback((fn: (data: any) => void) => {
    listenersRef.current.add(fn);
    return () => { listenersRef.current.delete(fn); };
  }, []);

  // Bot değiştirince localStorage favorilerini güncelle (Markets sayfası uyumluluğu)
  const setActiveBotId = useCallback((id: string) => {
    activeBotIdRef.current = id;
    setActiveBotIdState(id);
    const botFavs = botsStateRef.current[id]?.favorites || [];
    localStorage.setItem('raydium_favorites', JSON.stringify(botFavs));
    window.dispatchEvent(new Event('favorites_changed'));
  }, []);

  // setStrategies — aktif botun stratejilerini günceller, server'a gönderir
  const setStrategies: React.Dispatch<React.SetStateAction<Strategy[]>> = useCallback((action) => {
    const botId = activeBotIdRef.current;
    if (!botId) return;
    setBotsState(prev => {
      const existing = prev[botId] || DEFAULT_BOT_STATE;
      const next = typeof action === 'function' ? action(existing.strategies) : action;
      saveLocalStrategies(botId, next);
      if (syncedRef.current[botId] && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'update_strategies', bot_id: botId, payload: next }));
      }
      const updated = { ...prev, [botId]: { ...existing, strategies: next } };
      botsStateRef.current = updated;
      return updated;
    });
  }, []);

  // sendWs — global olmayan mesajlara otomatik bot_id ekler
  const sendWs = useCallback((msg: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // SIRA ÖNEMLİ: bot_id ÖNCE, msg SONRA. Tersi olunca çağıranın açıkça verdiği
      // bot_id eziliyor ve mesaj aktif bota gidiyordu — funding arb'da bacak emri
      // Extended yerine (o an aktif olan) salt-okunur Bluefin-LP köprüsüne düşmüştü.
      const outMsg = GLOBAL_MSG_TYPES.has(msg.type)
        ? msg
        : { bot_id: activeBotIdRef.current, ...msg };
      wsRef.current.send(JSON.stringify(outMsg));
    }
  }, []);

  // sendForBot — belirli bir bot'a mesaj gönderir (activeBotId'yi ezmez)
  const sendForBot = useCallback((msg: any, botId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ ...msg, bot_id: botId }));
    }
  }, []);

  // Bot state'ini güncelle (onmessage içinde kullanılır)
  const updateBotState = useCallback((botId: string, patch: Partial<BotState>) => {
    setBotsState(prev => {
      const updated = {
        ...prev,
        [botId]: { ...(prev[botId] || DEFAULT_BOT_STATE), ...patch },
      };
      botsStateRef.current = updated;
      return updated;
    });
  }, []);

  const clearCurrentToken = useCallback(() => {
    sessionStorage.removeItem(wsTokenKey(wsUrlRef.current));
  }, []);

  const switchEndpoint = useCallback((url: string) => {
    // GEÇERSİZ ADRES KORUMASI: boş dizge new WebSocket('') içinde sayfanın adresine
    // göre çözümleniyor ve tarayıcı "https://… isn't a valid URI: scheme isn't ws or
    // wss" diye ham bir hata fırlatıyor. Kullanıcı bunu emir popup'ında görüp emrin
    // reddedildiğini sanıyordu. Bağlantıya hiç dokunmadan, anlaşılır şekilde kes.
    if (!/^wss?:\/\//i.test(url || '')) {
      console.error('[ws] geçersiz endpoint, geçiş yapılmadı:', url || '(boş)');
      return;
    }
    // Aynı endpoint'e geçiş — gereksiz reconnect önle
    if (url === wsUrlRef.current && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    wsUrlRef.current = url;
    // Bazı botlar (Hyperliquid, Timur Bey, DeepBlue) AYRI bir backend'e bağlanıyor
    // ve o backend'ler yalnız kendi işlem mesajlarını tanıyor — SQLite geçmişi,
    // LP grupları gibi ortak veriler orada yok. Sayfaların bunu bilmesi gerekiyor,
    // yoksa cevabı sonsuza kadar bekliyorlar.
    setIsMainBackend(url === DEFAULT_WS_URL);
    // botsState kasıtlı sıfırlanmıyor: yeni endpoint'ten gelen veriler üzerine yazar.
    // Sıfırlama yapılırsa aynı endpoint'te geçiş sırasında "Pozisyon yok" flash'ı olur.
    setBotList([]);
    syncedRef.current = {};
    // Mevcut timer ve bağlantıyı temizle
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    if (pingTimer.current) clearInterval(pingTimer.current);
    if (wsRef.current) {
      const old = wsRef.current;
      wsRef.current = null;
      old.onopen    = null;
      old.onclose   = null;
      old.onerror   = null;
      old.onmessage = null;
      old.close();
    }
    setConnected(false);
    // Hemen yeni endpoint'e bağlan (2s gecikme olmadan)
    reconnectTimer.current = setTimeout(() => connectRef.current(), 300);
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING) return;

    const ws = new WebSocket(wsUrlRef.current);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      // GÜVENLİK: Backend artık auth gerektiriyor. Reconnect sonrası saklı token ile
      // otomatik yeniden yetkilen — yoksa tüm mesajlar 'auth_required' ile reddedilir.
      // Her endpoint kendi token'ını alır (her Python süreci ayrı token havuzu tutar).
      const tok = sessionStorage.getItem(wsTokenKey(wsUrlRef.current));
      if (tok) ws.send(JSON.stringify({ type: 'auth', payload: { token: tok } }));
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
      }, 30000);
    };

    ws.onclose = () => {
      setConnected(false);
      syncedRef.current = {};
      if (pingTimer.current) clearInterval(pingTimer.current);
      reconnectTimer.current = setTimeout(connect, 2000);
    };

    ws.onerror = () => { ws.close(); };

    ws.onmessage = (event) => {
      try {
        const data   = JSON.parse(event.data);
        const botId  = data.bot_id as string | undefined;

        switch (data.type) {

          // ── Auth — başarılı girişte token sakla (reconnect için) ────────────
          case 'auth_result':
            if (data.payload?.success && data.payload?.token) {
              // Bu endpoint'e özgü key ile sakla — farklı server'ların token'ları çakışmaz
              sessionStorage.setItem(wsTokenKey(wsUrlRef.current), data.payload.token);
            } else if (!data.payload?.success) {
              // Token geçersiz (server restart vb.) → bu endpoint'in token'ını temizle
              sessionStorage.removeItem(wsTokenKey(wsUrlRef.current));
            }
            break;

          // ── Bot-spesifik mesajlar ──────────────────────────────────────────
          case 'tickers':
            if (botId) updateBotState(botId, {
              tickers:    data.payload,
              lastUpdate: new Date().toLocaleTimeString('tr-TR'),
            });
            break;

          case 'account':
            if (botId) updateBotState(botId, { account: data.payload });
            break;

          case 'positions':
            if (botId) updateBotState(botId, { positions: data.payload });
            break;

          case 'coinm_state':
            if (botId) updateBotState(botId, { coinm: data.payload });
            break;

          // Pozisyon kapanınca sembol akıştan DÜŞÜYOR; birleştirmek yerine
          // komple değiştiriyoruz ki kapanmış pozisyonun fiyatı asılı kalmasın.
          case 'marks':
            if (botId) updateBotState(botId, {
              marks:   data.payload?.marks || {},
              marksTs: Number(data.payload?.ts || 0),
            });
            break;

          case 'open_orders':
            if (botId) updateBotState(botId, {
              openOrders: Array.isArray(data.payload) ? data.payload : [],
            });
            break;

          case 'order_counters':
            if (botId) updateBotState(botId, { orderCounters: data.payload });
            break;

          case 'order_stats':
            if (botId) updateBotState(botId, { orderStats: data.payload });
            break;

          case 'pair_trades':
            if (botId) updateBotState(botId, { pairTrades: data.payload || [] });
            break;

          case 'strategy_completed':
          case 'strategy_paused':
          case 'strategy_error':
            if (botId) {
              setBotsState(prev => {
                const existing = prev[botId] || DEFAULT_BOT_STATE;
                return {
                  ...prev,
                  [botId]: {
                    ...existing,
                    strategies: existing.strategies.map(s =>
                      s.id === data.payload?.strategy_id ? { ...s, active: false } : s
                    ),
                  },
                };
              });
            }
            break;

          case 'strategy_restarted':
            if (botId) {
              setBotsState(prev => {
                const existing = prev[botId] || DEFAULT_BOT_STATE;
                return {
                  ...prev,
                  [botId]: {
                    ...existing,
                    strategies: existing.strategies.map(s =>
                      s.id === data.payload?.strategy_id ? { ...s, active: true } : s
                    ),
                  },
                };
              });
            }
            break;

          // ── Manuel arb emri sonuçları — OrderModal'a ilet ──────────────
          case 'arb_order_result':
          case 'arb_order_result_ray': {
            window.dispatchEvent(new MessageEvent('ws_message', { data: JSON.stringify(data) }));
            break;
          }

          // ── Backend bildirimleri — toast + macOS browser notification ──────
          case 'notification': {
            const p = data.payload;
            if (p?.title) {
              showToast(p.title, p.message || '', 'info');
              // Bildirim sesi
              if (shouldPlaySound(p.title)) {
                playNotifSound();
              }
              // Browser (macOS) native notification
              if (typeof Notification !== 'undefined') {
                if (Notification.permission === 'granted') {
                  new Notification(p.title, { body: p.message || '', icon: '/favicon.ico' });
                } else if (Notification.permission !== 'denied') {
                  Notification.requestPermission().then(perm => {
                    if (perm === 'granted') {
                      new Notification(p.title, { body: p.message || '', icon: '/favicon.ico' });
                    }
                  });
                }
              }
            }
            break;
          }

          // ── Alert listesi güncellemesi ────────────────────────────────────
          case 'alerts_state': {
            const bid = botId || data.bot_id;
            if (bid) {
              setAlerts(prev => ({ ...prev, [bid]: data.payload || [] }));
            }
            break;
          }

          // ── Emir sonuçları — başarısızsa toast göster ──────────────────────
          case 'manual_order_result': {
            const p = data.payload;
            if (p && !p.success) {
              showToast(
                'Emir Reddedildi',
                p.error || p.msg || 'Emir gönderilemedi',
                'error',
              );
            }
            break;
          }

          case 'cancel_order_result': {
            // İptal SESSİZ KALMAMALI: eskiden sonuç hiç dönmüyordu, çarpı tuşu
            // tepkisiz görünüyordu (KuCoin'de köprü olmadığı için hep başarısızdı).
            const p = data.payload || {};
            if (p.success) showToast('Emir iptal edildi', p.symbol || '', 'info');
            else showToast('İptal edilemedi', p.error || 'bilinmeyen hata', 'error');
            break;
          }

          case 'order_error':
          case 'trade_error': {
            const p = data.payload;
            const msg = typeof p === 'string' ? p : (p?.error || p?.msg || p?.message || 'Bilinmeyen hata');
            showToast('Emir Hatası', msg, 'error');
            break;
          }

          case 'order_rejected': {
            const p = data.payload;
            const reason = typeof p === 'string' ? p : (p?.reason || p?.error || p?.msg || '');
            showToast('Emir Reddedildi', reason, 'error');
            break;
          }

          // Köprü sağlığı — bot bazında. Sunucu YALNIZ durum değiştiğinde
          // gönderiyor (sağlıklı→düştü / düştü→toparladı), her hatada değil.
          case 'bridge_status':
            if (botId) updateBotState(botId, { bridge: data.payload || { ok: true } });
            break;

          case 'market_info':
            if (botId) {
              setBotsMarketInfo(prev => ({ ...prev, [botId]: data.payload || {} }));
            }
            break;

          case 'kill_switch_activated':
            if (botId) {
              showToast('Kill Switch', 'Tüm stratejiler durduruldu', 'warning');
              setBotsState(prev => {
                const existing = prev[botId] || DEFAULT_BOT_STATE;
                return {
                  ...prev,
                  [botId]: { ...existing, strategies: existing.strategies.map(s => ({ ...s, active: false })) },
                };
              });
            }
            break;

          // ── restore_state — tüm botların başlangıç verisi ──────────────────
          case 'restore_state': {
            const botsPayload = data.payload?.bots as Record<string, any> | undefined;
            if (!botsPayload) break;

            const newBotList: BotInfo[] = Object.entries(botsPayload).map(([id, b]) => ({
              id,
              name:    (b as any).name    || id,
              network: (b as any).network || undefined,
            }));
            setBotList(newBotList);

            // İlk bağlantıda aktif bot seç
            if (!activeBotIdRef.current && newBotList.length > 0) {
              activeBotIdRef.current = newBotList[0].id;
              setActiveBotIdState(newBotList[0].id);
            }

            setBotsState(prev => {
              const next = { ...prev };
              for (const [bid, botData] of Object.entries(botsPayload)) {
                const bd           = botData as any;
                const existing     = prev[bid] || DEFAULT_BOT_STATE;
                const serverStrats: Strategy[] = bd.strategies || [];
                const serverFavs:   string[]   = bd.favorites  || [];
                const localStrats              = loadLocalStrategies(bid);

                let strategies: Strategy[];
                if (serverStrats.length > 0) {
                  strategies = serverStrats;
                  saveLocalStrategies(bid, serverStrats);
                } else if (localStrats.length > 0) {
                  strategies = localStrats;
                  pendingPushRef.current[bid] = localStrats;
                } else {
                  strategies = [];
                }

                syncedRef.current[bid] = true;
                next[bid] = { ...existing, strategies, pairTrades: bd.pairs || [], favorites: serverFavs };
              }
              botsStateRef.current = next;
              return next;
            });

            // Local stratejileri olan botları server'a push et
            for (const [bid, strats] of Object.entries(pendingPushRef.current)) {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'update_strategies', bot_id: bid, payload: strats }));
              }
            }
            pendingPushRef.current = {};
            break;
          }
        }

        listenersRef.current.forEach(fn => fn(data));
      } catch {}
    };
  }, [setStrategies, updateBotState]);

  // connectRef'i her zaman güncel tut (switchEndpoint circular dep olmadan kullanır)
  useEffect(() => { connectRef.current = connect; }, [connect]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  // Aktif botun verisini türet
  const activeBot = botsState[activeBotId] || DEFAULT_BOT_STATE;

  return (
    <WSContext.Provider value={{
      connected,
      tickers:        activeBot.tickers,
      account:        activeBot.account,
      positions:      activeBot.positions,
      openOrders:     activeBot.openOrders,
      orderCounters:  activeBot.orderCounters,
      orderStats:     activeBot.orderStats,
      strategies:     activeBot.strategies,
      lastUpdate:     activeBot.lastUpdate,
      pairTrades:     activeBot.pairTrades,
      coinm:          activeBot.coinm,
      setStrategies,
      sendWs,
      sendForBot,
      subscribe,
      switchEndpoint,
      isMainBackend,
      clearCurrentToken,
      botList,
      activeBotId,
      setActiveBotId,
      botsState,
      alerts,
      favorites:      activeBot.favorites,
      marketInfo:     botsMarketInfo[activeBotId] || {},
      bridge:         activeBot.bridge || { ok: true },
    }}>
      {children}
    </WSContext.Provider>
  );
}
